import React, { useState } from 'react';
import styled from 'styled-components';

interface CoinPreference {
  symbol: string;
  name: string;
  isSelected: boolean;
  longEnabled: boolean;
  shortEnabled: boolean;
}

const SinyalAyarlari: React.FC = () => {
  // Örnek coin listesi (backend'den gelecek)
  const [coins, setCoins] = useState<CoinPreference[]>([
    { symbol: 'BTC', name: 'Bitcoin', isSelected: false, longEnabled: false, shortEnabled: false },
    { symbol: 'ETH', name: 'Ethereum', isSelected: false, longEnabled: false, shortEnabled: false },
    { symbol: 'BNB', name: 'Binance Coin', isSelected: false, longEnabled: false, shortEnabled: false },
    { symbol: 'SOL', name: 'Solana', isSelected: false, longEnabled: false, shortEnabled: false },
    { symbol: 'ADA', name: 'Cardano', isSelected: false, longEnabled: false, shortEnabled: false },
  ]);

  const toggleCoinSelection = (symbol: string) => {
    setCoins(coins.map(coin => 
      coin.symbol === symbol 
        ? { ...coin, isSelected: !coin.isSelected }
        : coin
    ));
  };

  const toggleLong = (symbol: string) => {
    setCoins(coins.map(coin => 
      coin.symbol === symbol 
        ? { ...coin, longEnabled: !coin.longEnabled }
        : coin
    ));
  };

  const toggleShort = (symbol: string) => {
    setCoins(coins.map(coin => 
      coin.symbol === symbol 
        ? { ...coin, shortEnabled: !coin.shortEnabled }
        : coin
    ));
  };

  const savePreferences = () => {
    // Backend'e kaydetme işlemi
    const selectedCoins = coins.filter(coin => coin.isSelected);
    console.log('Kaydedilen tercihler:', selectedCoins);
  };

  return (
    <Container>
      <Header>
        <Title>Sinyal Ayarları</Title>
        <SaveButton onClick={savePreferences}>
          <SaveIcon>💾</SaveIcon>
          Kaydet
        </SaveButton>
      </Header>

      <Description>
        Sinyal almak istediğiniz coinleri ve pozisyon tiplerini seçin.
      </Description>

      <CoinList>
        {coins.map(coin => (
          <CoinCard key={coin.symbol} $isSelected={coin.isSelected}>
            <CoinHeader>
              <CoinInfo>
                <CoinSymbol>{coin.symbol}</CoinSymbol>
                <CoinName>{coin.name}</CoinName>
              </CoinInfo>
              <SelectButton 
                onClick={() => toggleCoinSelection(coin.symbol)}
                $isSelected={coin.isSelected}
              >
                {coin.isSelected ? '✓' : '+'}
              </SelectButton>
            </CoinHeader>

            {coin.isSelected && (
              <PositionTypes>
                <PositionToggle 
                  onClick={() => toggleLong(coin.symbol)}
                  $isEnabled={coin.longEnabled}
                  $type="LONG"
                >
                  <ToggleIcon>📈</ToggleIcon>
                  LONG
                </PositionToggle>
                <PositionToggle 
                  onClick={() => toggleShort(coin.symbol)}
                  $isEnabled={coin.shortEnabled}
                  $type="SHORT"
                >
                  <ToggleIcon>📉</ToggleIcon>
                  SHORT
                </PositionToggle>
              </PositionTypes>
            )}
          </CoinCard>
        ))}
      </CoinList>
    </Container>
  );
};

const Container = styled.div`
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #F1F5F9;
  margin: 0;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #3B82F6;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: #2563EB;
    transform: translateY(-2px);
  }
`;

const SaveIcon = styled.span`
  font-size: 1.2rem;
`;

const Description = styled.p`
  color: #94A3B8;
  font-size: 1.1rem;
  margin-bottom: 32px;
`;

const CoinList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
`;

const CoinCard = styled.div<{ $isSelected: boolean }>`
  background: rgba(30, 41, 59, 0.5);
  border-radius: 16px;
  padding: 20px;
  border: 2px solid ${props => props.$isSelected ? '#3B82F6' : 'transparent'};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  }
`;

const CoinHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CoinInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CoinSymbol = styled.span`
  font-size: 1.4rem;
  font-weight: 600;
  color: #F1F5F9;
`;

const CoinName = styled.span`
  font-size: 0.9rem;
  color: #94A3B8;
`;

const SelectButton = styled.button<{ $isSelected: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$isSelected ? '#3B82F6' : 'rgba(59, 130, 246, 0.1)'};
  color: ${props => props.$isSelected ? 'white' : '#3B82F6'};
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$isSelected ? '#2563EB' : 'rgba(59, 130, 246, 0.2)'};
  }
`;

const PositionTypes = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
`;

const PositionToggle = styled.button<{ $isEnabled: boolean; $type: 'LONG' | 'SHORT' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border-radius: 12px;
  border: none;
  background: ${props => props.$isEnabled 
    ? props.$type === 'LONG' 
      ? 'rgba(16, 185, 129, 0.2)' 
      : 'rgba(239, 68, 68, 0.2)'
    : 'rgba(30, 41, 59, 0.5)'
  };
  color: ${props => props.$isEnabled
    ? props.$type === 'LONG'
      ? '#10B981'
      : '#EF4444'
    : '#94A3B8'
  };
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$type === 'LONG' 
      ? 'rgba(16, 185, 129, 0.3)' 
      : 'rgba(239, 68, 68, 0.3)'
    };
  }
`;

const ToggleIcon = styled.span`
  font-size: 1.2rem;
`;

export default SinyalAyarlari;